/**
 * 接单面板的**纵向几何契约**（Task 24 验收缺陷「第一次点接单被吃掉」的判据载体）。
 *
 * **为什么存在**：jsdom 没有排版引擎，`getBoundingClientRect()` 恒为全 0 ——
 * 「解锁提示条出现后，接单列表的可点击几何有没有变」在组件测试里**没有别的办法测量**。
 * 而这条位移正是真浏览器实测到的缺陷：首次 `pointerdown` 解锁提示音 ⇒ 提示条插进列表
 * **上方**的文档流 ⇒ 同一击的 `pointerdown` 与 `pointerup` 落在不同元素 ⇒
 * 浏览器不派发 `click` ⇒ 收银员点两次才接单成功。
 *
 * **机制**：面板/卡片把自己声明过的纵向度量（容器间距与宽度、条件渲染子元素的高度、
 * 出流浮层的视口框）以 `data-geom-*` 属性挂在 DOM 上；`CashierBoardPage.spec.tsx` 里的
 * 布局模型读这些属性算出元素几何并做**命中判定**（`document.elementFromPoint` 的替身）。
 * 于是「插进流里 ⇒ 整体下移 ⇒ 同一坐标落到别的元素」这条真浏览器因果链在 jsdom 里可断言。
 *
 * ⚠️ **改了 `CashierBoardPage` 的可见结构或 Tailwind 间距，必须同步改 `GEOM`**：
 * 属性缺失时模型按 0 高度算，几何判据会退化成恒真的 `0 ≈ 0`（spec 里有一条活性对照
 * 专门盯住这点：拿不到 ≥2 个带高度断言的元素就报错，而不是静默变绿）。
 *
 * 这些属性是**只读度量**，不参与任何行为；删掉它们面板照样工作，只是失去几何可测性。
 */

/** 根容器的 `gap-3`（12px）与视口宽度基准（面板宽度不参与命中判定，只用于生成矩形的 right） */
export const BOARD_GEOM_GAP = 12;
export const BOARD_GEOM_WIDTH = 640;

interface GeomBox {
  /** 在文档流里的作者高度；缺省 = 不占纵向空间（浮层） */
  readonly h?: number;
  /** 出流浮层：距视口底边 */
  readonly fixedBottom?: number;
  /** 出流浮层：距视口左边 */
  readonly fixedLeft?: number;
  /** 出流浮层宽度（要足够小，不能铺满左列的按钮，否则提示条自己就成了吞点击的遮罩） */
  readonly fixedWidth?: number;
}

/**
 * 几何表按**元素自己的语义 id**（`data-geom`）取，不按 DOM 下标：
 * 错误条 / 解锁提示条 / 离线提示都是条件渲染的，下标会整体错位。
 *
 * 只列到「待接单列表里的接单按钮」为止：它**之后**的兄弟（如右上角的新单浮层）
 * 不影响它的纵向位置（flex 列流只累加它前面的兄弟）。
 */
export const BOARD_GEOM: Readonly<Record<string, GeomBox>> = {
  control: { h: 44 }, // 扫码取单 / 手输短码 / 手动建单
  tabs: { h: 40 }, // 四个分区 tab
  count: { h: 20 }, // order-card-count
  // Phase 9（R-06 ④）：`board-data-time` 的「数据时间」标注 + 陈旧警示，`text-sm` 单行。
  // ⚠️ 它**在文档流里**（夹在 `count` 与列表之间）⇒ 不给高度的话，模型算出的接单按钮 y
  // 会比真浏览器小一行，命中判定就落在一个真实存在的元素之外的坐标上（几何判据假绿）。
  'data-time': { h: 20 },
  'board-error': { h: 40 }, // p-2 + text-sm 单行
  // 解锁提示条：**出流浮层**，左下角（`bottom-[calc(4.5rem+…)] left-4`），不铺满按钮
  'sound-ready': { h: 38, fixedBottom: 72, fixedLeft: 16, fixedWidth: 284 },
  'offline-hint': { h: 28 },
  listitem: {}, // 卡片容器：自身高度不影响按钮（按钮的流只由同父的前兄弟决定）
  'accept-button': { h: 44 },
};

/** 几何表 → `data-geom-*` 属性（`id` 同时写进 `data-geom`，便于 spec 与调试定位） */
export function geomAttrs(id: keyof typeof BOARD_GEOM | string): Record<string, string> {
  const geom: GeomBox | undefined = BOARD_GEOM[id];
  if (geom === undefined) return {};
  const attrs: Record<string, string> = { 'data-geom': id };
  if (geom.h !== undefined) attrs['data-geom-height'] = String(geom.h);
  if (geom.fixedBottom !== undefined) attrs['data-geom-fixed-bottom'] = String(geom.fixedBottom);
  if (geom.fixedLeft !== undefined) attrs['data-geom-fixed-left'] = String(geom.fixedLeft);
  if (geom.fixedWidth !== undefined) attrs['data-geom-fixed-width'] = String(geom.fixedWidth);
  return attrs;
}
